import React, { useEffect, useState, useCallback } from "react";
import ProductTable from "./products/ProductTable";
import { Plus, ArrowUpRight } from "lucide-react";
import EmptyState from "./EmptyState";
import { Card, CardContent } from "./ui/card.tsx";
import { useStripeConnection } from "../hooks/useStripeConnection.ts";
import { useServerPaginatedProduct } from "../hooks/useServerPaginatedProduct.ts";
import TablePagination from "./ui/tablePagination.tsx";
import { useProductCreatedListener } from "../hooks/useGlobalEvents.ts";

interface ProductListServerPaginatedProps {
    filterStatus?: string;
    searchQuery?: string;
    includeSubscriptions?: boolean;
    includeOneTime?: boolean;
}

const ProductListServerPaginated: React.FC<ProductListServerPaginatedProps> = ({
    filterStatus,
    searchQuery,
    includeSubscriptions = true,
    includeOneTime = true,
}) => {
    const { isConnected: stripeConnected, isLoading} = useStripeConnection();

    useProductCreatedListener(() => {
        refresh();
    });
    
    const {
        products,
        loading,
        error,
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        hasNextPage,
        hasPreviousPage,
        startItem,
        endItem,
        goToPage,
        goToNextPage,
        goToPreviousPage,
        goToFirstPage,
        goToLastPage,
        setItemsPerPage,
        setFilters,
        refresh,
    } = useServerPaginatedProduct({
      initialFilters:{
        status: filterStatus,
        searchQuery,
        includeSubscriptions,
        includeOneTime,
      },
      initialItemsPerPage: 10,  
    });

    useEffect(() => {
        const newFilters: {
            status?: string;
            searchQuery?: string;
            billing_period?: string;
            includeSubscriptions?: boolean;
            includeOneTime?: boolean;
        } = {};

        if (filterStatus) newFilters.status = filterStatus;
        if (searchQuery) newFilters.searchQuery = searchQuery;

        if(!includeSubscriptions && includeOneTime) {
            newFilters.billing_period = "one_time";
        } else if (includeSubscriptions && !includeOneTime) {
            newFilters.billing_period = "subscription";
        }

        newFilters.includeSubscriptions = includeSubscriptions;
        newFilters.includeOneTime = includeOneTime;

        setFilters(newFilters);
    }, [filterStatus, searchQuery, includeSubscriptions, includeOneTime, setFilters]);

    return (
        <Card className="border shadow-sm">
            <CardContent className="p-0">
                {loading || isLoading ? (
                    <div className="text-center py-12 bg-card">
                        <p className="text-muted-foreground">Loading products...</p>
                    </div>
                ) : !stripeConnected ? (
                    <EmptyState
                        title="Connect your Stripe account"
                        description="To start creating products and receiving payments, connect your Stripe account first."
                        actionLabel="Connect Stripe Account"
                        identifier
                        icon={<ArrowUpRight className="h-12 w-12" />}
                    />
                ) : error ? (
                    <EmptyState
                        title="Error loading products"
                        description={error}
                        actionLabel="Try Again"
                        actionLink="/retry"
                        icon={<Plus className="h-12 w-12" />}
                    />
                ) : products.length === 0 ? (
                    <EmptyState
                        title="No products found"
                        description="Create your first product with a sharable payment link."
                        actionLabel={null}
                        actionLink={null}
                        icon={<Plus className="h-12 w-12" />}
                    />
                ) : (
                    <>  
                        <ProductTable
                            products={products}
                            includeSample={false}
                            onArchive={refresh}
                        />
                        <TablePagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            itemsPerPage={itemsPerPage}
                            totalItems={totalItems}
                            startItem={startItem}
                            endItem={endItem}
                            hasNextPage={hasNextPage}
                            hasPreviousPage={hasPreviousPage}
                            onPageChange={goToPage}
                            onItemsPerPageChange={setItemsPerPage}
                            onFirstPage={goToFirstPage}
                            onLastPage={goToLastPage}
                            onNextPage={goToNextPage}
                            onPreviousPage={goToPreviousPage}
                        />
                    </>
                    )}
            </CardContent>
        </Card>
    )
}
export default ProductListServerPaginated;